/**
 * Lookbook Component - Custom Element
 * Handles hotspot pins, product card popover, boundary collision, and Shopify Theme Editor events
 */
class LookbookComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.hotspots = this.querySelectorAll('.lookbook__hotspot');
    this.mediaContainer = this.querySelector('.lookbook__media') || this;

    this.initEventListeners();
    this.initThemeEditorEvents();

    // Check if any card has is-active on initial load
    const initialActive = this.querySelector('.lookbook__card.is-active');
    if (initialActive) {
      const parentHotspot = initialActive.closest('.lookbook__hotspot');
      if (parentHotspot) {
        parentHotspot.classList.add('is-active');
        this.adjustCardPosition(initialActive);
      }
    }
  }

  initEventListeners() {
    if (!this.hotspots) return;

    this.hotspots.forEach((hotspot) => {
      const dot = hotspot.querySelector('.lookbook__dot');
      const pill = hotspot.querySelector('.lookbook__pill');
      const card = hotspot.querySelector('.lookbook__card');
      const closeBtn = hotspot.querySelector('.lookbook__card-close');

      const toggleCard = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const isOpen = card && card.classList.contains('is-active');
        if (isOpen) {
          this.closeHotspot(hotspot);
        } else {
          this.openHotspot(hotspot);
        }
      };

      if (dot) dot.addEventListener('click', toggleCard);
      if (pill) pill.addEventListener('click', toggleCard);

      if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.closeHotspot(hotspot);
        });
      }

      if (card) {
        card.addEventListener('click', (e) => {
          e.stopPropagation();
        });
      }
    });

    // Close when clicking anywhere outside this lookbook
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.closeAll();
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        this.closeAll();
      }
    });

    // Re-adjust card positioning on window resize
    window.addEventListener('resize', () => {
      const activeCard = this.querySelector('.lookbook__card.is-active');
      if (activeCard) {
        this.adjustCardPosition(activeCard);
      }
    });
  }

  openHotspot(targetHotspot) {
    this.closeAll();
    targetHotspot.classList.add('is-active');
    const card = targetHotspot.querySelector('.lookbook__card');
    const dot = targetHotspot.querySelector('.lookbook__dot');
    if (dot) dot.setAttribute('aria-expanded', 'true');
    if (card) {
      card.classList.add('is-active');
      this.adjustCardPosition(card);
    }
  }

  closeHotspot(targetHotspot) {
    targetHotspot.classList.remove('is-active');
    const card = targetHotspot.querySelector('.lookbook__card');
    const dot = targetHotspot.querySelector('.lookbook__dot');
    if (dot) dot.setAttribute('aria-expanded', 'false');
    if (card) {
      card.classList.remove('is-active');
      card.classList.remove('pos-flipped-x', 'pos-flipped-y');
    }
  }

  closeAll() {
    if (!this.hotspots) return;
    this.hotspots.forEach((hotspot) => {
      this.closeHotspot(hotspot);
    });
  }

  adjustCardPosition(card) {
    if (!card) return;
    card.classList.remove('pos-flipped-x', 'pos-flipped-y');

    requestAnimationFrame(() => {
      const cardRect = card.getBoundingClientRect();
      const containerRect = (this.mediaContainer || this).getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // Check right overflow
      if (cardRect.right > containerRect.right || cardRect.right > viewportWidth - 16) {
        card.classList.add('pos-flipped-x');
      }

      // Check left overflow
      if (cardRect.left < containerRect.left || cardRect.left < 16) {
        card.classList.remove('pos-flipped-x');
      }

      // Check top overflow
      if (cardRect.top < containerRect.top || cardRect.top < 16) {
        card.classList.add('pos-flipped-y');
      }
    });
  }

  initThemeEditorEvents() {
    document.addEventListener('shopify:block:select', (event) => {
      if (!this.contains(event.target)) return;
      const blockId = event.detail.blockId;
      const targetHotspot = this.querySelector(`[data-block-id="${blockId}"]`);
      if (targetHotspot) {
        this.openHotspot(targetHotspot);
      }
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      if (!this.contains(event.target)) return;
      const blockId = event.detail.blockId;
      const targetHotspot = this.querySelector(`[data-block-id="${blockId}"]`);
      if (targetHotspot) {
        this.closeHotspot(targetHotspot);
      }
    });
  }
}

if (!customElements.get('lookbook-component')) {
  customElements.define('lookbook-component', LookbookComponent);
}

/**
 * RevealSection Component - Custom Element
 * Observes sections inside and applies reveal-in animation on scroll
 */
class RevealSection extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    this.initObserver();

    const run = () => {
      this.observeSections();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }
    setTimeout(run, 150);

    this.initThemeEditorEvents();
  }

  initObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            this.observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -40px 0px',
        threshold: 0.05
      }
    );
  }

  observeSections() {
    const sections = this.querySelectorAll('.shopify-section');
    if (!sections.length) return;

    sections.forEach((section, index) => {
      // Text scroll is a ticker/announcement section - keep visible immediately
      if (section.classList.contains('section-text-scroll') || section.querySelector('text-scroll')) {
        section.classList.add('reveal-section', 'is-revealed');
        return;
      }

      // Apple 3D or video section manages its own physics - do NOT add reveal-section to prevent any translateY/opacity jitter
      if (section.querySelector('[data-apple-3d-card], .video-section, video, iframe')) {
        return;
      }

      // If section contains multiple columns, products, or cards, enable sequential stagger reveal
      const items = section.querySelectorAll('.grid__item, .multicolumn-list__item, .collage__item');
      if (items.length > 1) {
        section.classList.add('reveal-section--stagger');
        items.forEach((item, i) => {
          const delay = (i * 0.22 + 0.12).toFixed(2);
          item.style.setProperty('--item-delay', `${delay}s`);
        });
      }

      const isAboveFold = index === 0;

      if (isAboveFold) {
        if (section.classList.contains('reveal-section--stagger')) {
          section.classList.add('reveal-section');
          setTimeout(() => {
            section.classList.add('is-revealed');
          }, 150);
        } else {
          section.classList.add('reveal-section', 'is-revealed');
        }
      } else {
        section.classList.add('reveal-section');
        this.observer.observe(section);
      }
    });
  }

  initThemeEditorEvents() {
    document.addEventListener('shopify:section:load', (event) => {
      const newSection = event.target;
      if (newSection && this.contains(newSection)) {
        const items = newSection.querySelectorAll('.grid__item, .multicolumn-list__item, .collage__item');
        if (items.length > 1) {
          newSection.classList.add('reveal-section--stagger');
          items.forEach((item, i) => {
            const delay = (i * 0.22 + 0.12).toFixed(2);
            item.style.setProperty('--item-delay', `${delay}s`);
          });
        }
        newSection.classList.add('reveal-section');
        setTimeout(() => {
          newSection.classList.add('is-revealed');
        }, 120);
      }
    });
  }
}

if (!customElements.get('reveal-section')) {
  customElements.define('reveal-section', RevealSection);
}

/**
 * Parallax Banner Image (Smooth Scroll Translate)
 */
function initBannerParallax() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const banners = document.querySelectorAll('.banner__media.animate--parallax');
  if (!banners.length) return;

  const visibleBanners = new Set();
  let ticking = false;

  function updateParallax() {
    ticking = false;
    const vh = window.innerHeight;
    const viewportCenter = vh / 2;

    visibleBanners.forEach((bannerMedia) => {
      const rect = bannerMedia.getBoundingClientRect();
      const bannerCenter = rect.top + rect.height / 2;
      const distance = bannerCenter - viewportCenter;

      const speed = 0.15;
      let offset = -distance * speed;

      const maxOffset = rect.height * 0.07;
      offset = Math.max(-maxOffset, Math.min(maxOffset, offset));

      const targets = bannerMedia.querySelectorAll('img, svg');
      targets.forEach((el) => {
        el.style.setProperty('--parallax-offset', `${offset.toFixed(1)}px`);
      });
    });
  }

  function requestTick() {
    if (!ticking && visibleBanners.size > 0) {
      ticking = true;
      requestAnimationFrame(updateParallax);
    }
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          visibleBanners.add(entry.target);
        } else {
          visibleBanners.delete(entry.target);
        }
      });
      requestTick();
    },
    { rootMargin: '50px 0px 50px 0px' }
  );

  banners.forEach((banner) => observer.observe(banner));

  window.addEventListener('scroll', requestTick, { passive: true });
  window.addEventListener('resize', requestTick, { passive: true });
  requestTick();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBannerParallax);
} else {
  initBannerParallax();
}

document.addEventListener('shopify:section:load', () => {
  initBannerParallax();
});

/**
 * Text Scroll (Marquee) Component - Custom Element
 * References HDT Marquee with dynamic clones & Web Animations API
 */
class TextScroll extends HTMLElement {
  #resizeObserver;

  constructor() {
    super();
    this._animation = null;
    this.#resizeObserver = null;
    this._lastWidth = 0;
    this._moveDistance = 0;
    this._durationMs = 0;
    this._scrollVelocity = 0;
    this._rafId = null;
    this._lastScrollY = 0;
    this._isVisible = true;
    this._intersectionObserver = null;
    this._onScroll = null;
    this._onWheel = null;
  }

  connectedCallback() {
    this._inner = this.querySelector('.text-scroll__inner');
    if (!this._inner) {
      const originalItem = this.querySelector('.text-scroll__item');
      if (!originalItem) return;
      this.innerHTML = '';
      this._inner = document.createElement('div');
      this._inner.className = 'text-scroll__inner';
      this.appendChild(this._inner);
      this._inner.appendChild(originalItem.cloneNode(true));
    }

    this._setup();

    if (window.ResizeObserver) {
      this.#resizeObserver = new ResizeObserver(() => this._setup());
      this.#resizeObserver.observe(this);
    } else {
      this._onResize = () => this._setup();
      window.addEventListener('resize', this._onResize);
    }

    // Hover pause
    if (this.hasAttribute('pausable')) {
      this.addEventListener('mouseenter', () => this._animation?.pause());
      this.addEventListener('mouseleave', () => this._animation?.play());
    }

    // Scroll with mouse interaction
    if (this.hasAttribute('scroll-with-mouse')) {
      this._initScrollWithMouse();
    }

    // Shopify Theme Editor support
    this._initThemeEditorEvents();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        this._lastWidth = 0;
        this._setup();
      });
    }
  }

  disconnectedCallback() {
    this._lastWidth = 0;
    this._animation?.cancel();
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
    }
    if (this._onScroll) {
      window.removeEventListener('scroll', this._onScroll);
      this._onScroll = null;
    }
    if (this._onWheel) {
      window.removeEventListener('wheel', this._onWheel);
      this._onWheel = null;
    }
    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect();
      this._intersectionObserver = null;
    }
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  _initScrollWithMouse() {
    this._lastScrollY = window.scrollY;

    // Observe visibility so we only update when section is in or near viewport
    if (window.IntersectionObserver) {
      this._intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this._isVisible = entry.isIntersecting;
          });
        },
        { rootMargin: '200px 0px' }
      );
      this._intersectionObserver.observe(this);
    } else {
      this._isVisible = true;
    }

    const applyScrollDelta = (deltaY) => {
      if (!this._isVisible || !this._animation || !this._durationMs) return;

      // Gentle velocity input: smooth and refined without jumping
      const addedVelocity = deltaY * 0.035;
      this._scrollVelocity = Math.max(-15, Math.min(15, this._scrollVelocity + addedVelocity));

      if (!this._rafId) {
        this._rafId = requestAnimationFrame(this._updateScrollVelocity.bind(this));
      }
    };

    let lastScrollTime = 0;
    this._onScroll = () => {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - this._lastScrollY;
      this._lastScrollY = currentScrollY;
      lastScrollTime = performance.now();
      if (delta !== 0) {
        applyScrollDelta(delta);
      }
    };

    this._onWheel = (e) => {
      // If at top or bottom boundary of page, or direct wheel on element when page doesn't scroll
      const isAtTop = window.scrollY <= 0 && e.deltaY < 0;
      const isAtBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2 &&
        e.deltaY > 0;
      const recentScroll = performance.now() - lastScrollTime < 50;

      if ((isAtTop || isAtBottom || !recentScroll) && Math.abs(e.deltaY) > 0) {
        if (isAtTop || isAtBottom) {
          applyScrollDelta(e.deltaY * 0.15);
        }
      }
    };

    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('wheel', this._onWheel, { passive: true });
  }

  _updateScrollVelocity() {
    if (Math.abs(this._scrollVelocity) > 0.02 && this._animation && this._durationMs) {
      // Soft timeShift: creates a natural, gentle glide instead of racing fast
      const timeShift = this._scrollVelocity * (this._durationMs / Math.max(this._moveDistance, 100)) * 0.18;
      const cur = this._animation.currentTime;
      if (typeof cur === 'number' && !isNaN(cur)) {
        let next = cur + timeShift;
        if (next < 0) {
          next = (next % this._durationMs) + this._durationMs;
        }
        try {
          this._animation.currentTime = next;
        } catch (e) {
          // ignore potential edge-cases
        }
      }

      this._scrollVelocity *= 0.92; // Higher inertia decay = softer, silkier glide
      this._rafId = requestAnimationFrame(this._updateScrollVelocity.bind(this));
    } else {
      this._scrollVelocity = 0;
      this._rafId = null;
    }
  }

  _setup() {
    if (!this._inner || !this._inner.firstElementChild) return;

    // Read measurements before DOM mutation to eliminate forced reflow
    const containerWidth = this.offsetWidth;
    const firstWidth = this._inner.firstElementChild.offsetWidth;
    if (firstWidth === 0 || this._lastWidth === firstWidth) return;
    this._lastWidth = firstWidth;

    const clonesNeeded = Math.max(Math.ceil((containerWidth * 3) / firstWidth), 2);

    // Remove previously generated duplicates
    this.querySelectorAll('.text-scroll--duplicate').forEach((e) => e.remove());

    const frag = document.createDocumentFragment();
    for (let i = 0; i < clonesNeeded; i++) {
      const clone = this._inner.firstElementChild.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.classList.add('text-scroll--duplicate');
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => {
        el.removeAttribute('data-shopify-editor-block');
      });
      clone.querySelectorAll('a, button').forEach((el) => {
        el.tabIndex = -1;
      });
      frag.appendChild(clone);
    }
    this._inner.appendChild(frag);

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const rawDirection = this.getAttribute('direction') || 'rtl';
    const isRTL = document.dir === 'rtl' || document.documentElement.getAttribute('dir') === 'rtl';
    const direction = isRTL ? (rawDirection === 'rtl' ? 'ltr' : 'rtl') : rawDirection;

    const speed = 300 * parseFloat(this.getAttribute('speed-factor') || '0.4'); // Move px/s
    const moveDistance = firstWidth;
    const duration = moveDistance / speed; // seconds

    this._moveDistance = moveDistance;
    this._durationMs = duration * 1000;

    const from = direction === 'rtl' ? '0px' : `-${moveDistance}px`;
    const to = direction === 'rtl' ? `-${moveDistance}px` : '0px';

    this._animation?.cancel();
    this._animation = this._inner.animate(
      [
        { transform: `translateX(${from}) translateZ(0)` },
        { transform: `translateX(${to}) translateZ(0)` }
      ],
      {
        duration: duration * 1000,
        easing: 'linear',
        iterations: Infinity
      }
    );
  }

  _initThemeEditorEvents() {
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target && event.target.contains(this)) {
        this._lastWidth = 0;
        this._setup();
      }
    });

    document.addEventListener('shopify:block:select', (event) => {
      if (event.target && this.contains(event.target)) {
        this._lastWidth = 0;
        this._setup();
      }
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      if (event.target && this.contains(event.target)) {
        this._lastWidth = 0;
        this._setup();
      }
    });
  }
}

if (!customElements.get('text-scroll')) {
  customElements.define('text-scroll', TextScroll);
}
/**
 * Helper: Setup clean continuous loop & autoplay for HTML5 video
 * Allows native GPU-accelerated looping without disruptive seek operations or micro-stutters
 */
function setupSeamlessVideo(video) {
  if (!video || video._hasLoopGuard) return;
  video._hasLoopGuard = true;

  video.muted = true;
  video.setAttribute('muted', '');
  video.playsInline = true;
  video.setAttribute('playsinline', '');
  video.loop = true;
  video.setAttribute('loop', '');
  video.setAttribute('webkit-playsinline', '');

  // Safe loop fallback without manual seek (seeking drops hardware decoded buffers and freezes video)
  video.addEventListener('ended', () => {
    if (video.loop && video.paused) {
      const playPromise = video.play();
      if (playPromise !== undefined) playPromise.catch(() => {});
    }
  });
}

/**
 * Slideshow Video Autoplay & Pause Controller
 * Observes the entire slideshow container (not individual horizontal slides) to prevent
 * intersection fluttering, and controls videos smoothly based on active slide.
 */
function initSlideshowVideos() {
  const slideshows = document.querySelectorAll('slideshow-component');
  if (!slideshows.length) return;

  slideshows.forEach((slideshow) => {
    if (slideshow._hasSlideshowVideoInit) return;
    slideshow._hasSlideshowVideoInit = true;

    // Helper: update which video in this slideshow should play
    const updateActiveVideo = () => {
      if (!slideshow._isInViewport) return;

      const slides = slideshow.querySelectorAll('.slideshow__slide');
      const currentPage = slideshow.currentPage || 1;
      const currentSlide = slides[currentPage - 1] || slides[0];

      slides.forEach((slide) => {
        const videos = slide.querySelectorAll('video');
        if (slide === currentSlide) {
          videos.forEach((video) => {
            // Only play if video is actually visible (check classes/styles to avoid forced reflow)
            const isHidden =
              (window.innerWidth < 750 && video.classList.contains('hidden_mobile')) ||
              (window.innerWidth >= 750 && video.classList.contains('hidden_desktop')) ||
              video.style.display === 'none';
            if (isHidden) {
              video._shouldBePlaying = false;
              if (!video.paused) {
                video.pause();
              }
              return;
            }

            setupSeamlessVideo(video);
            video._shouldBePlaying = true;
            video._userPaused = false;

            // Pause slideshow auto-rotate during video playback
            if (typeof slideshow.pause === 'function') {
              slideshow.pause();
            }
            if (slideshow.autoplay) {
              clearInterval(slideshow.autoplay);
            }

            if (video.paused) {
              const playPromise = video.play();
              if (playPromise !== undefined) {
                playPromise.catch(() => {
                  video.muted = true;
                  video.play().catch(() => {});
                });
              }
            }
          });
        } else {
          videos.forEach((video) => {
            video._shouldBePlaying = false;
            if (!video.paused) {
              video.pause();
            }
          });
        }
      });
    };

    // Pause all videos when slideshow is off screen
    const pauseAllSlideshowVideos = () => {
      slideshow.querySelectorAll('video').forEach((video) => {
        video._shouldBePlaying = false;
        if (!video.paused) {
          video.pause();
        }
      });
    };

    // 1. Observe entire slideshow container for vertical screen entry/exit
    if (window.IntersectionObserver) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              slideshow._isInViewport = true;
              updateActiveVideo();
            } else {
              slideshow._isInViewport = false;
              pauseAllSlideshowVideos();
            }
          });
        },
        { rootMargin: '200px 0px 200px 0px', threshold: 0 }
      );
      observer.observe(slideshow);
    } else {
      slideshow._isInViewport = true;
      updateActiveVideo();
    }

    // 2. Listen to slide changes (clicking arrows, dots, or swiping)
    slideshow.addEventListener('slideChanged', () => {
      updateActiveVideo();
    });

    // Also update on scroll of the slider container
    const slider = slideshow.querySelector('[id^="Slider-"]');
    if (slider) {
      let scrollTimer = null;
      slider.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(updateActiveVideo, 80);
      }, { passive: true });
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSlideshowVideos);
} else {
  initSlideshowVideos();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target && event.target.querySelector('slideshow-component')) {
    initSlideshowVideos();
  }
});

/**
 * Video Section Autoplay Controller
 * Observes stable outer container to prevent 3D perspective flickering,
 * and maintains continuous uninterrupted playback with loop safeguards.
 */
function initVideoSectionAutoplay() {
  if (!window.IntersectionObserver) return;

  // 1. Process all HTML5 videos in video sections
  const videoElements = document.querySelectorAll('.video-section video, [data-apple-3d-card] video');
  videoElements.forEach((video) => {
    setupSeamlessVideo(video);

    // Observe the STABLE outer section container (not the 3D-transformed child)
    const stableContainer =
      video.closest('.video-section--apple-3d') ||
      video.closest('.video-section') ||
      video.closest('.shopify-section') ||
      video.parentElement;

    if (stableContainer && !stableContainer._hasVideoObserver) {
      stableContainer._hasVideoObserver = true;

      const containerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const innerVideos = entry.target.querySelectorAll('video');
            if (entry.isIntersecting) {
              innerVideos.forEach((v) => {
                if (v.paused) {
                  const playPromise = v.play();
                  if (playPromise !== undefined) {
                    playPromise.catch(() => {
                      v.muted = true;
                      v.play().catch(() => {});
                    });
                  }
                }
              });
            } else {
              innerVideos.forEach((v) => {
                if (!v.paused) {
                  v.pause();
                }
              });
            }
          });
        },
        {
          root: null,
          rootMargin: '350px 0px 350px 0px',
          threshold: 0
        }
      );

      containerObserver.observe(stableContainer);
    }
  });

  // 2. Process all YouTube / Vimeo iframes
  const iframeElements = document.querySelectorAll('.video-section iframe, [data-apple-3d-card] iframe');
  iframeElements.forEach((iframe) => {
    const stableContainer =
      iframe.closest('.video-section--apple-3d') ||
      iframe.closest('.video-section') ||
      iframe.closest('.shopify-section') ||
      iframe.parentElement;

    if (stableContainer && !stableContainer._hasIframeObserver) {
      stableContainer._hasIframeObserver = true;

      const iframeObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const iframes = entry.target.querySelectorAll('iframe');
            if (entry.isIntersecting) {
              iframes.forEach((f) => {
                try {
                  if (f.classList.contains('js-youtube') || (f.src && f.src.includes('youtube.com'))) {
                    f.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
                  } else if (f.classList.contains('js-vimeo') || (f.src && f.src.includes('vimeo.com'))) {
                    f.contentWindow?.postMessage('{"method":"play"}', '*');
                  }
                } catch (e) {}
              });
            } else {
              iframes.forEach((f) => {
                try {
                  if (f.classList.contains('js-youtube') || (f.src && f.src.includes('youtube.com'))) {
                    f.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
                  } else if (f.classList.contains('js-vimeo') || (f.src && f.src.includes('vimeo.com'))) {
                    f.contentWindow?.postMessage('{"method":"pause"}', '*');
                  }
                } catch (e) {}
              });
            }
          });
        },
        {
          root: null,
          rootMargin: '350px 0px 350px 0px',
          threshold: 0
        }
      );

      iframeObserver.observe(stableContainer);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVideoSectionAutoplay);
} else {
  initVideoSectionAutoplay();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target && event.target.querySelector('video, iframe, deferred-media')) {
    initVideoSectionAutoplay();
  }
});

/**
 * Apple 3D Perspective Video Scroll Controller
 * Calculates scroll progress for 3D tilt flip and expand-to-full-width
 */
let isApple3DScrollBound = false;

function updateApple3DProgress() {
  const cards = document.querySelectorAll('[data-apple-3d-card]');
  if (!cards.length) return;

  const windowHeight = window.innerHeight || document.documentElement.clientHeight;

  cards.forEach((card) => {
    const stage = card.closest('.video-section__apple-3d-stage') || card;
    const rect = stage.getBoundingClientRect();

    const startY = windowHeight;
    const endY = windowHeight * 0.18;

    let p = 0;
    if (rect.top >= startY) {
      p = 0;
    } else if (rect.top <= endY) {
      p = 1;
    } else {
      const raw = (startY - rect.top) / (startY - endY);
      const clamped = Math.max(0, Math.min(1, raw));
      p = 0.5 - Math.cos(clamped * Math.PI) / 2;
    }

    const formattedP = p.toFixed(4);
    if (card._lastP !== formattedP) {
      card._lastP = formattedP;
      card.style.setProperty('--apple-p', formattedP);
    }
  });
}

function initApple3DVideoScroll() {
  updateApple3DProgress();

  if (!isApple3DScrollBound) {
    let ticking = false;
    const requestTick = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateApple3DProgress();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', requestTick, { passive: true });
    isApple3DScrollBound = true;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApple3DVideoScroll);
} else {
  initApple3DVideoScroll();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target && event.target.querySelector('[data-apple-3d-card]')) {
    initApple3DVideoScroll();
  }
});

document.addEventListener('shopify:section:select', (event) => {
  if (event.target && event.target.querySelector('[data-apple-3d-card]')) {
    initApple3DVideoScroll();
  }
});

/**
 * SEO & Accessibility: Ensure all images (including video poster fallbacks) have alt attributes
 */
function fixMissingImageAlts() {
  const images = document.querySelectorAll('video img:not([alt]), .video-section img:not([alt]), img:not([alt])');
  if (!images.length) return;

  images.forEach((img) => {
    const video = img.closest('video');
    const section = img.closest('.video-section, .shopify-section');
    const heading = section?.querySelector('.title, h1, h2, h3')?.textContent?.trim();
    const fallbackText = heading || video?.getAttribute('aria-label') || document.title || 'Happy Clothing';
    img.setAttribute('alt', fallbackText);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', fixMissingImageAlts);
} else {
  fixMissingImageAlts();
}

document.addEventListener('shopify:section:load', fixMissingImageAlts);



